/**
 * googleAuthUrl — builds the Google sign-in URL the user is redirected to.
 *
 * Strategy
 * --------
 * If `REACT_APP_GOOGLE_CLIENT_ID` is set (self-owned OAuth credentials),
 * build the direct `accounts.google.com` URL — the consent screen reads
 * "Continue to Shopperden" because that's the registered OAuth client's
 * brand.
 *
 * If the env var is missing (e.g. during a transitional deploy where
 * .env hasn't propagated yet), fall back to Emergent's managed bridge
 * so signups don't break. The branding is "Continue to emergentagent.com"
 * in that case, but it still works.
 *
 * State CSRF guard
 * ----------------
 * We generate a random `state` and stash it in sessionStorage. AuthCallback
 * verifies the value on return to defeat CSRF / open-redirect attacks.
 */

const GOOGLE_AUTH_ENDPOINT = "https://accounts.google.com/o/oauth2/v2/auth";
const STATE_KEY = "shopperden_google_state";

function generateState() {
  // 22 chars of base64-url entropy is plenty for CSRF
  const arr = new Uint8Array(16);
  if (window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(arr);
  } else {
    for (let i = 0; i < 16; i++) arr[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(arr)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Build the URL to send the user to.
 * @param {string} redirectUrl - absolute URL of /auth/callback
 * @returns {string} - URL to assign to window.location.href
 */
export function buildGoogleAuthUrl(redirectUrl) {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  if (clientId) {
    const state = generateState();
    try { sessionStorage.setItem(STATE_KEY, state); } catch { /* ignore */ }

    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUrl,
      response_type: "code",
      scope: "openid email profile",
      state,
      // `select_account` lets a user pick which Google account if logged
      // into multiple — much better UX than silently using the last one.
      prompt: "select_account",
      access_type: "online",
    });
    return `${GOOGLE_AUTH_ENDPOINT}?${params.toString()}`;
  }

  // Fallback — legacy Emergent bridge
  return `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

/**
 * Validate the state param when AuthCallback returns from Google.
 * Returns true if state matches (or if there's no stored state — which
 * means this is the legacy bridge flow that doesn't use state).
 */
export function verifyGoogleState(returnedState) {
  if (!returnedState) {
    // Legacy bridge flow uses URL fragments — no state to compare.
    return true;
  }
  try {
    const stored = sessionStorage.getItem(STATE_KEY);
    if (stored && stored === returnedState) {
      sessionStorage.removeItem(STATE_KEY);
      return true;
    }
    return false;
  } catch {
    return true;
  }
}
